<template>
  <v-navigation-drawer
    fixed
    right
    temporary
    class="h-100"
    overlay-opacity="0.7"
    width="410"
    :permanent="isRenameOpen || isDeleteDialogOpen"
    :value="visible"
    @input="checkDrawerValue"
  >
    <v-card elevation="0" class="h-100" :loading="isLoading">
      <div class="px-4 py-6">
        <p class="h6 px-3 mb-4">
          {{ $t('filters.my_filter_other') }} ({{ totalRecords }})
        </p>
        <SearchInput class="px-2 mb-4" :value="terms" @change="handleSearch" />
        <v-list
          class="px-2 pb-3"
          style="height: 78vh; overflow: scroll;"
          v-if="hasUserFilters"
        >
          <v-list-item
            v-for="userFilter in userFilters"
            :key="userFilter._id"
            class="px-1 w-100 d-block"
          >
            <div class="d-flex justify-space-between">
              <text-highlight
                highlightClass="yellow lighten-1"
                class="body2 pr-3 mx-0 blue-grey--text text--darken-3 text-truncate"
                highlightComponent="span"
                :queries="highlightQueries"
                :title="userFilter.name"
                >{{ userFilter.name }}</text-highlight
              >
              <div class="d-flex">
                <v-icon
                  class="mr-2 cursor-pointer"
                  :color="userFilter.isPinned ? 'blue' : 'blue-grey'"
                  @click="onPinUserFilter(userFilter)"
                >
                  {{ userFilter.isPinned ? 'mdi-pin' : 'mdi-pin-outline' }}
                </v-icon>
                <v-menu offset-y nudge-bottom="5">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" class="cursor-pointer" color="blue-grey"
                      >mdi-dots-vertical</v-icon
                    >
                  </template>
                  <v-list dense>
                    <v-list-item
                      class="mx-1 pl-3 pr-4 cursor-pointer"
                      @click="handleRename(userFilter)"
                    >
                      <v-icon
                        class="mr-3"
                        color="blue-grey darken-1 blue-grey--text text--darken-3"
                        >mdi-pencil-outline</v-icon
                      >
                      <span class="mx-0 px-0 caption">{{
                        $t('common.rename')
                      }}</span>
                    </v-list-item>
                    <v-list-item
                      class="mx-1 pl-3 pr-4 cursor-pointer"
                      @click="handleDelete(userFilter)"
                    >
                      <v-icon
                        class="mr-3"
                        color="blue-grey darken-1 blue-grey--text text--darken-3"
                        >mdi-delete-outline</v-icon
                      >
                      <span class="mx-0 px-0 caption">{{
                        $t('common.button.delete')
                      }}</span>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </v-list-item>
        </v-list>
        <EmptyState
          v-if="!hasUserFilters && !isLoading"
          class="px-6 py-6"
          icon="mdi-ghost-outline"
          :iconSize="32"
          :title="$t('common.no_results_found')"
          :message="$t('common.no_matches_text')"
        />
        <div class="w-100 fixed-bottom">
          <v-pagination
            v-if="totalPages > 1"
            circle
            class="my-4 elevation-0"
            total-visible="3"
            :length="totalPages"
            :value="currentPage"
            @input="onPageChange"
          />
        </div>
      </div>
    </v-card>
    <DeleteConfirmationModal
      :show="isDeleteDialogOpen"
      :title="$t('insights.delete_filter')"
      :text="$t('filters.delete_filter_text')"
      @cancel="isDeleteDialogOpen = false"
      @delete="onDeleteUserFilter"
    />
    <UserFiltersRename
      v-if="isRenameOpen"
      :userFilter="userFilter"
      :isLoading="isLoading"
      @close="isRenameOpen = false"
      @update="onRenameUserFilter"
    />
  </v-navigation-drawer>
</template>

<script>
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'
import { mapGetters } from 'vuex'

import EmptyState from '@/components/EmptyState.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

import UserFiltersRename from './UserFiltersRename.vue'

const ITEMS_PER_PAGE = 15

export default {
  props: {
    visible: Boolean,
  },
  components: {
    EmptyState,
    SearchInput,
    DeleteConfirmationModal,
    UserFiltersRename,
  },
  mixins: [mixin, trackingMixin],
  data() {
    return {
      userFilters: [],
      terms: '',
      userFilter: {},
      currentPage: 1,
      totalPages: 0,
      totalRecords: 0,
      isLoading: true,
      isDeleteDialogOpen: false,
      isRenameOpen: false,
    }
  },
  mounted() {
    this.fetchUserFilters()
  },
  computed: {
    ...mapGetters(['organizationId']),
    highlightQueries() {
      return [this.terms ?? '', ...(this.terms?.split(' ') || [])]
    },
    hasUserFilters() {
      return this.userFilters?.length
    },
    activeUserFilter() {
      return this.$route.query.userFilter || ''
    },
  },
  methods: {
    async fetchUserFilters() {
      try {
        this.isLoading = true
        const response = await api.userFilter.getAll(this.organizationId, {
          limit: ITEMS_PER_PAGE,
          skip: (this.currentPage - 1) * ITEMS_PER_PAGE,
          sort: { by: 'isPinned', order: 'desc' },
          terms: this.terms,
        })
        this.userFilters = response?.data?.results
        this.totalRecords = response?.data?.totalRecordsInData
        this.totalPages = Math.ceil(this.totalRecords / ITEMS_PER_PAGE) || 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
    onPinUserFilter(userFilter) {
      try {
        userFilter.isPinned = !userFilter.isPinned
        this.onUpdateUserFilter(userFilter)
        this.trackFilterActions('my_filters.pin', userFilter)
      } catch (err) {
        userFilter.isPinned = !userFilter.isPinned
        return this.handleRequestError(err)
      }
    },
    async onRenameUserFilter(name) {
      await this.onUpdateUserFilter({ ...this.userFilter, name })
      this.fetchUserFilters()
      this.isRenameOpen = false
      this.trackFilterActions('my_filters.rename', name)
    },
    async onUpdateUserFilter(userFilter) {
      try {
        this.isLoading = true
        await api.userFilter.update(
          this.organizationId,
          userFilter._id,
          userFilter
        )
        this.$emit('onChange')
      } catch (err) {
        if (err.response.data.errorCode === 'USF002')
          return this.showWarning(this.$t('explore.only_three_pinned'))
        return this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
    async onDeleteUserFilter() {
      try {
        this.isLoading = true
        await api.userFilter.delete(this.organizationId, this.userFilter?._id)
        if (this.userFilter?._id === this.activeUserFilter)
          this.$emit('onRemoveActiveFilter')
        this.$emit('onChange')
        this.fetchUserFilters()
        this.trackFilterActions('my_filters.delete', this.userFilter)
      } catch (err) {
        return this.handleRequestError(err)
      } finally {
        this.isLoading = false
        this.isDeleteDialogOpen = false
      }
    },
    handleRename(userFilter) {
      this.isRenameOpen = true
      this.userFilter = userFilter
    },
    handleDelete(userFilter) {
      this.isDeleteDialogOpen = true
      this.userFilter = userFilter
    },
    onPageChange(page) {
      if (this.currentPage === page) return
      this.currentPage = page
      this.fetchUserFilters()
      this.trackFilterActions('my_filters.pagination_drawer', page)
    },
    checkDrawerValue(val) {
      if (val) return
      this.$emit('close')
    },
    handleSearch(terms) {
      this.terms = terms
    },
  },
  watch: {
    terms: _.debounce(function() {
      this.fetchUserFilters()
      this.trackFilterActions('my_filters.search_drawer', this.terms)
    }, 800),
  },
}
</script>

<style lang="scss" scoped>
.fixed-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
}
</style>
